import { useMemo } from 'react';
import { LabelItem } from '../types/LabelItem';
import { LabelLayout, LabelSheet } from '../types/LabelLayout';

/** Number of labels per A4 sheet (3 columns × 8 rows) */
const LABELS_PER_SHEET = 24;

/**
 * Calculates label layout for multi-sheet printing.
 *
 * Divides labels across multiple A4 sheets, with each sheet holding
 * up to 24 labels in a 3×8 grid. Empty slots are filled with null.
 *
 * @param items - Array of label items to layout
 * @param startPosition - Starting cell position (0-23) for first label. Defaults to 0.
 *                        Use this when printing on partially used label sheets.
 * @returns Layout structure with sheets, counts, and empty slots
 */
export function calculateLayout(
    items: LabelItem[],
    startPosition: number = 0
): LabelLayout {
    // Validate startPosition
    const validStartPosition = Math.max(
        0,
        Math.min(startPosition, LABELS_PER_SHEET - 1)
    );

    const totalLabels = items.length;
    // Calculate total slots needed including the offset from startPosition
    const totalSlotsNeeded = totalLabels + validStartPosition;
    const totalSheets = Math.ceil(totalSlotsNeeded / LABELS_PER_SHEET);
    const sheets: LabelSheet[] = [];

    // Create array with null padding at the start
    const paddedItems: (LabelItem | null)[] = [
        ...Array(validStartPosition).fill(null),
        ...items,
    ];

    for (let i = 0; i < totalSheets; i++) {
        const start = i * LABELS_PER_SHEET;
        const end = start + LABELS_PER_SHEET;
        const sheetLabels = paddedItems.slice(start, end);

        // Pad with nulls to fill 24 slots
        while (sheetLabels.length < LABELS_PER_SHEET) {
            sheetLabels.push(null);
        }

        sheets.push({
            sheetNumber: i + 1,
            labels: sheetLabels,
        });
    }

    const unusedLabelSlots = totalSheets * LABELS_PER_SHEET - totalSlotsNeeded;

    return {
        sheets,
        totalSheets,
        totalLabels,
        unusedLabelSlots,
    };
}

/**
 * Hook to calculate print layout from label items.
 *
 * Memoizes the layout calculation to avoid unnecessary recomputation.
 *
 * @param items - Array of label items to layout
 * @param startPosition - Starting cell position (0-23) for first label. Defaults to 0.
 */
export function usePrintLayout(
    items: LabelItem[],
    startPosition: number = 0
): LabelLayout {
    return useMemo(
        () => calculateLayout(items, startPosition),
        [items, startPosition]
    );
}
